import { queryOptions } from "@tanstack/react-query";

import { ContentLanguageHeaderName } from "@/api/ApiClientProvider";
import { useApi } from "@/api/hooks/useApi";
import type { SurveyDetailsV2Dto } from "@/api/types";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import type { ApiQueryParams } from "@/types/api-types";
import type { EntityDetailsQueryOptions } from "@/types/query-types";

export const useSurveyQueries = () => {
  const api = useApi();
  const projectId = useProjectId();

  return {
    list: ({ query }: { query: ApiQueryParams<"getSurveysV1"> }) =>
      queryOptions({
        queryKey: QUERY_KEYS.SURVEYS_QUERY(projectId, query),
        queryFn: () => api.getSurveysV1(query),
        select: commonAPIDataSelector,
      }),
    details: (
      {
        surveyId,
        category,
      }: {
        surveyId: string;
        category: SurveyDetailsV2Dto["category"];
      },
      options?: EntityDetailsQueryOptions,
    ) => {
      const headers = {
        [ContentLanguageHeaderName]: options?.translationMode || "default",
      };

      return queryOptions({
        queryKey: QUERY_KEYS.SURVEY_DETAILS(projectId, surveyId, options),
        queryFn: () =>
          api.getSurveysDetailsV2(
            surveyId,
            {
              includeRankingQuestion: category === "feelAtHome",
            },
            { headers },
          ),
        select: commonAPIDataSelector,
      });
    },
    suggestedFollowUps: (surveyId: string) =>
      queryOptions({
        queryKey: QUERY_KEYS.SURVEY_SUGGESTED_FOLLOW_UPS(projectId, surveyId),
        queryFn: () => api.getSurveysSuggestedFollowUpsV1(surveyId),
        select: commonAPIDataSelector,
        staleTime: Infinity,
      }),
    export: ({ surveyId, query }: { surveyId: string; query?: ApiQueryParams<"getSurveysExportV2", 1> }) =>
      queryOptions({
        queryKey: QUERY_KEYS.SURVEY_EXPORT(projectId, surveyId, query),
        queryFn: () => api.getSurveysExportV2(surveyId, query).then((x) => x.blob()),
      }),
    responseSentimentSummary: (surveyId: string, questionId: string) =>
      queryOptions({
        queryKey: QUERY_KEYS.SURVEY_DETAILS_RESPONSE_SUMMARY(projectId, surveyId, questionId),
        queryFn: () => api.getSurveysSummaryV1(surveyId, questionId),
        select: commonAPIDataSelector,
        staleTime: 1000 * 60 * 5,
      }),
    massSurveyGroups: () =>
      queryOptions({
        queryKey: QUERY_KEYS.MASS_SURVEY_GROUPS(),
        queryFn: () => api.getMassSurveysGroupsV1(),
        select: commonAPIDataSelector,
      }),
    massSurveyProjects: (groupId?: string) =>
      queryOptions({
        queryKey: QUERY_KEYS.MASS_SURVEY_PROJECTS(groupId),
        queryFn: () => api.getMassSurveysProjectsV1(groupId ? { GroupId: groupId } : undefined),
        select: commonAPIDataSelector,
      }),
  };
};
